<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <!-- HEADER -->
    <header class="bg-[#1a1a1a] text-white shadow sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        <router-link to="/" class="flex items-center gap-2 cursor-pointer">
          <img src="/brand/getlance-logo.svg" alt="Logo" class="h-20 w-auto" />
        </router-link>
        <div class="flex gap-3">
          <router-link to="/login"
            class="px-4 py-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition whitespace-nowrap">
            Fazer login
          </router-link>
        </div>
      </div>
    </header>

    <!-- FORMULÁRIO DE CADASTRO -->
    <section class="flex-1 flex items-center justify-center px-6 py-16">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#e0dcd6] overflow-hidden">
        <h2 class="text-3xl font-bold text-[#152a34] mb-1">Criar conta</h2>
        <p class="text-gray-500 mb-8">Leva menos de 1 minuto.</p>

        <!-- NOME -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">Nome completo</label>
          <input type="text" v-model="form.name" placeholder="Seu nome" class="input-gold" />
        </div>

        <!-- EMAIL -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">E-mail</label>
          <input
            type="email"
            v-model="form.email"
            placeholder="seuemail@exemplo.com"
            class="input-gold"
            :class="emailError ? 'border-red-400 focus:ring-red-300' : ''"
            @input="emailError = ''"
            @blur="onEmailBlur"
          />
          <p v-if="emailError" class="text-red-500 text-xs mt-1">{{ emailError }}</p>
        </div>

        <!-- SENHA -->
        <div class="mb-5">
          <label class="block text-sm font-medium mb-1">Senha</label>
          <input type="password" v-model="form.password" placeholder="********" class="input-gold" />
        </div>

        <!-- TIPO DE ATUAÇÃO -->
        <div class="mb-1">
          <label class="block text-sm font-semibold text-[#152a34] mb-2">Como você vai usar a plataforma?</label>
          <div class="flex flex-col gap-2">
            <label
              v-for="opt in providerTypeOptions"
              :key="opt.value"
              class="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition"
              :class="form.provider_type === opt.value
                ? 'bg-[#152a34] text-white border-[#152a34]'
                : 'bg-white border-gray-200 hover:border-[#b99a52]'"
            >
              <input type="radio" v-model="form.provider_type" :value="opt.value" class="sr-only" />
              <span class="mdi text-lg flex-shrink-0 mt-0.5" :class="opt.icon"></span>
              <div>
                <p class="font-semibold text-sm leading-tight">{{ opt.label }}</p>
                <p class="text-xs mt-0.5 opacity-70">{{ opt.description }}</p>
              </div>
            </label>
          </div>
        </div>

        <!-- CEP (condicional) -->
        <transition name="slide-down">
          <div v-if="needsCep" class="mt-4 p-4 bg-[#fdf8f0] border border-[#e9dfc9] rounded-2xl">
            <label class="block text-sm font-semibold text-[#152a34] mb-1">
              CEP de atuação <span class="text-red-500">*</span>
            </label>
            <p class="text-xs text-gray-500 mb-2">Informe o CEP da região onde você atua para aparecer nas buscas.</p>
            <div class="flex gap-2">
              <input
                v-model="form.zipCode"
                @input="onCepInput"
                @blur="onCepBlur"
                type="text"
                placeholder="00000-000"
                maxlength="9"
                class="input-gold flex-1"
                :class="cepError ? 'border-red-400 focus:ring-red-300' : ''"
              />
              <button
                type="button"
                @click="fetchCep"
                :disabled="cepLoading"
                class="px-3 py-2 bg-[#152a34] text-white rounded-xl text-sm hover:bg-[#1e3a4a] transition disabled:opacity-60 flex items-center justify-center min-w-[44px]"
              >
                <span v-if="cepLoading" class="mdi mdi-loading animate-spin"></span>
                <span v-else class="mdi mdi-magnify"></span>
              </button>
            </div>
            <p v-if="cepError" class="text-red-500 text-xs mt-1">{{ cepError }}</p>
            <div v-if="form.service_address" class="mt-2 p-2 bg-white rounded-xl text-xs text-[#152a34] border border-[#e9dfc9]">
              <span class="mdi mdi-map-marker text-[#b99a52] mr-1"></span>
              {{ form.service_address.address ? form.service_address.address + ', ' : '' }}{{ form.service_address.neighborhood }} — {{ form.service_address.city }}/{{ form.service_address.state }}
            </div>
          </div>
        </transition>

        <!-- BOTÃO CRIAR CONTA -->
        <button @click="register"
          class="w-full py-3 rounded-full bg-[#b99a52] hover:bg-[#a18446] text-white font-semibold transition disabled:opacity-50 mt-6"
          :disabled="isSubmitting">
          <span v-if="!isSubmitting">Criar conta</span>
          <span v-else>Cadastrando...</span>
        </button>

        <!-- DIVISOR -->
        <div class="flex items-center my-6">
          <div class="flex-grow border-t border-gray-300"></div>
          <span class="mx-3 text-gray-500 text-sm">ou</span>
          <div class="flex-grow border-t border-gray-300"></div>
        </div>

        <p v-if="needsCep" class="text-amber-700 text-sm mb-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          <span class="mdi mdi-information-outline mr-1"></span>
          Para cadastro <strong>presencial</strong> ou <strong>ambos</strong>, preencha o CEP acima antes de continuar com o Google.
        </p>

        <!-- GOOGLE SIGNUP -->
        <div class="mb-6">
          <div ref="googleBtnWrap" class="w-full overflow-hidden">
            <div ref="googleBtn" class="w-full flex justify-center"></div>
          </div>
        </div>

        <!-- LOGIN LINK -->
        <p class="text-center text-gray-600 text-sm mt-6">
          Já tem conta?
          <router-link to="/login" class="text-[#b99a52] font-semibold hover:underline">
            Fazer login
          </router-link>
        </p>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import api from "@/services/api";
import AppFooter from "@/components/AppFooter.vue";

const router = useRouter();
const store = useStore();

const GOOGLE_CLIENT_ID = "708117913743-j1tmpf9b8endvjd3im363cniugltsvjd.apps.googleusercontent.com";

const googleBtn = ref(null);
const googleBtnWrap = ref(null);

const providerTypeOptions = [
  {
    value: 'remote',
    label: 'Somente Remoto',
    description: 'Quero contratar ou oferecer serviços e projetos remotos (design, dev, redação, etc.).',
    icon: 'mdi-laptop',
  },
  {
    value: 'presential',
    label: 'Somente Presencial',
    description: 'Quero oferecer serviços presenciais (personal trainer, eletricista, pintor, etc.).',
    icon: 'mdi-map-marker-account',
  },
  {
    value: 'both',
    label: 'Ambos',
    description: 'Quero atuar tanto em projetos remotos quanto em serviços presenciais.',
    icon: 'mdi-account-multiple',
  },
]

const form = ref({
  name: "",
  email: "",
  password: "",
  provider_type: "remote",
  zipCode: "",
  service_address: null,
  latitude: null,
  longitude: null,
});

const isSubmitting = ref(false);
const cepLoading = ref(false);
const cepError = ref("");
const emailError = ref("");

const needsCep = computed(() => ['presential', 'both'].includes(form.value.provider_type))

function validateEmailDomainTypos(email) {
  const value = String(email || '').trim().toLowerCase()
  if (!value) return null

  const badDomains = [
    { bad: '@gmail.con', good: '@gmail.com' },
    { bad: '@gmal.com', good: '@gmail.com' },
  ]

  const hit = badDomains.find(d => value.includes(d.bad))
  if (!hit) return null

  return `Domínio de e-mail parece incorreto (${hit.bad}). Você quis dizer ${hit.good}?`
}

function onEmailBlur() {
  emailError.value = validateEmailDomainTypos(form.value.email) || ""
}

function formatCep(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length <= 5) return digits
  return digits.slice(0, 5) + '-' + digits.slice(5, 9)
}

function onCepInput(e) {
  form.value.zipCode = formatCep(e.target.value)
  cepError.value = ""
  form.value.service_address = null
  form.value.latitude = null
  form.value.longitude = null
}

function onCepBlur() {
  const digits = form.value.zipCode.replace(/\D/g, '')
  if (digits.length === 8) fetchCep()
}

async function fetchCep() {
  const cep = form.value.zipCode.replace(/\D/g, '')
  if (cep.length !== 8) return
  cepLoading.value = true
  cepError.value = ""
  try {
    const { data } = await api.get(`/cep?cep=${cep}`)
    if (data && !data.erro) {
      const addr = {
        zip_code: cep,
        address: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || ''
      }
      form.value.service_address = addr
      await geocodeAddress(addr)
    } else {
      cepError.value = "CEP não encontrado. Verifique e tente novamente."
    }
  } catch {
    cepError.value = "Erro ao consultar o CEP."
  } finally {
    cepLoading.value = false
  }
}

async function geocodeAddress(addr) {
  const key = process.env.VUE_APP_GOOGLE_MAPS_API_KEY
  if (!key) return
  try {
    const addressStr = [addr.address, addr.neighborhood, addr.city, addr.state, 'Brasil'].filter(Boolean).join(', ')
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressStr)}&key=${key}&language=pt-BR`
    const res = await fetch(url)
    const data = await res.json()
    if (data.status === 'OK' && data.results?.[0]) {
      const loc = data.results[0].geometry.location
      form.value.latitude = loc.lat
      form.value.longitude = loc.lng
    }
  } catch {}
}

function validateForm() {
  const errors = [];
  const data = form.value;

  if (!data.name.trim()) errors.push("O nome é obrigatório.");
  if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
    errors.push("E-mail inválido ou obrigatório.");
  const domainTypoError = validateEmailDomainTypos(data.email)
  if (domainTypoError) {
    emailError.value = domainTypoError
    errors.push(domainTypoError)
  }
  if (!data.password) errors.push("A senha é obrigatória.");
  else if (data.password.length < 6)
    errors.push("A senha deve ter no mínimo 6 caracteres.");

  if (needsCep.value) {
    const cep = data.zipCode.replace(/\D/g, '')
    if (cep.length !== 8) errors.push("Informe um CEP válido com 8 dígitos.")
    else if (!data.service_address) errors.push("Aguarde a confirmação do CEP antes de continuar.")
  }

  return errors;
}

async function register() {
  const errors = validateForm();
  if (errors.length) {
    Swal.fire({
      icon: "warning",
      title: "Corrija os campos",
      html: errors.map(e => `<p>${e}</p>`).join(''),
    });
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = {
      name: form.value.name,
      email: form.value.email,
      password: form.value.password,
      role: "user",
      provider_type: form.value.provider_type,
    };

    if (needsCep.value && form.value.service_address) {
      payload.service_address = form.value.service_address;
      if (form.value.latitude) payload.latitude = form.value.latitude;
      if (form.value.longitude) payload.longitude = form.value.longitude;
    }

    await api.post("/register", payload);

    await Swal.fire({
      icon: "info",
      title: "Seu cadastro está quase pronto!",
      html: `Enviamos um e-mail de confirmação.<br/><br/><b>Verifique sua caixa de entrada ou spam</b> e clique no botão para liberar sua conta.`,
      confirmButtonText: "Entendi"
    });

    router.push("/login");
  } catch (error) {
    const code = error.response?.data?.code;
    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({
        icon: "warning",
        title: "Conta pendente",
        html: `Enviamos um e-mail para <b>${form.value.email}</b>.<br/>Confira a caixa de entrada e o spam.`,
      });
      return;
    }
    const errorMsg = error?.response?.data?.error || "Tente novamente mais tarde.";
    Swal.fire({ icon: "error", title: "Erro ao cadastrar", text: errorMsg });
  } finally {
    isSubmitting.value = false;
  }
}

const waitForGoogle = (timeoutMs = 5000) => {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (window.google?.accounts?.id) return resolve(true);
      if (Date.now() - start >= timeoutMs) return reject(new Error("timeout"));
      setTimeout(tick, 50);
    };
    tick();
  });
};

function renderGoogleButton() {
  if (!googleBtn.value || !googleBtnWrap.value) return;
  const wrapWidth = googleBtnWrap.value.clientWidth || 0;
  const buttonWidth = Math.min(360, Math.max(240, wrapWidth));
  googleBtn.value.innerHTML = "";
  window.google.accounts.id.renderButton(googleBtn.value, {
    theme: "outline",
    size: "large",
    text: "signup_with",
    shape: "pill",
    width: buttonWidth,
    logo_alignment: "left",
  });
}

onMounted(async () => {
  await nextTick();
  try {
    await waitForGoogle(5000);
  } catch {
    return;
  }
  if (!googleBtn.value || !googleBtnWrap.value) return;

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: handleGoogleCallback,
  });

  renderGoogleButton();
  window.addEventListener("resize", renderGoogleButton);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", renderGoogleButton);
});

function validateCepForGoogle() {
  if (!needsCep.value) return null;
  const data = form.value;
  const cep = data.zipCode.replace(/\D/g, "");
  if (cep.length !== 8) return "Informe um CEP válido com 8 dígitos.";
  if (!data.service_address) return "Aguarde a confirmação do CEP (clique na lupa ou saia do campo) antes de continuar com o Google.";
  return null;
}

const handleGoogleCallback = async (response) => {
  const cepErrorMsg = validateCepForGoogle();
  if (cepErrorMsg) {
    Swal.fire({
      icon: "warning",
      title: "CEP obrigatório",
      text: "Para uso presencial ou ambos, é obrigatório informar um CEP válido. Preencha o CEP acima e aguarde a confirmação antes de continuar com o Google.",
    });
    return;
  }

  try {
    isSubmitting.value = true;
    const payload = {
      credential: response.credential,
      provider_type: form.value.provider_type,
    };
    if (needsCep.value && form.value.service_address) {
      payload.service_address = form.value.service_address;
      if (form.value.latitude != null) payload.latitude = form.value.latitude;
      if (form.value.longitude != null) payload.longitude = form.value.longitude;
    }
    const result = await store.dispatch("signInWithGoogle", payload);
    if (result?.token) {
      router.push("/");
      return;
    }
    Swal.fire({ icon: "error", title: "Erro ao entrar com Google.", text: "Resposta inválida do servidor." });
  } catch (error) {
    const code = error.response?.data?.code;
    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({ icon: "warning", title: "Conta pendente", text: "Confira a caixa de entrada do e-mail utilizado no Google." });
      return;
    }
    Swal.fire({ icon: "error", title: "Erro ao entrar com Google.", text: error.response?.data?.error || "Tente novamente mais tarde." });
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}
.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 300px;
}
</style>
